import { observable, action, runInAction, computed } from 'mobx';
import {
    BlocklistService,
    BlocklistEntry,
    BlocklistResponse,
    FetchBlocklistParams
} from '../services/BlocklistService';
import Store from '.';

export class BlocklistStore {
    @observable public blocklistEntries: BlocklistEntry[] = [];
    @observable public loading: boolean = false;
    @observable public error: string | null = null;
    @observable public totalCount: number = 0;
    @observable public currentPage: number = 1;
    @observable public pageSize: number = 25;

    private service: BlocklistService;

    constructor(store: Store) {
        this.service = new BlocklistService(store.api.client);
    }

    @computed
    get totalPages(): number {
        return Math.ceil(this.totalCount / this.pageSize);
    }

    @computed
    get hasEntries(): boolean {
        return this.blocklistEntries.length > 0;
    }

    @action.bound
    public async fetchBlocklist(companyId: number, page: number = 1): Promise<void> {
        this.loading = true;
        this.error = null;
        this.currentPage = page;

        try {
            const offset = (page - 1) * this.pageSize;
            const params: FetchBlocklistParams = {
                limit: this.pageSize,
                offset,
                order: 'created_at:desc'
            };

            const response: BlocklistResponse = await this.service.fetchBlocklist(companyId, params);

            runInAction(() => {
                this.blocklistEntries = response.blocklist;
                this.totalCount = response.count;
                this.loading = false;
            });
        } catch (error) {
            runInAction(() => {
                this.error = error.response?.data?.message || 'Failed to fetch blocklist';
                this.loading = false;
            });
        }
    }

    @action.bound
    public async addEmail(companyId: number, email: string): Promise<boolean> {
        this.loading = true;
        this.error = null;

        try {
            const newEntry = await this.service.addEmail(companyId, email);

            runInAction(() => {
                // Add to the beginning of the list
                this.blocklistEntries = [newEntry, ...this.blocklistEntries];
                this.totalCount += 1;
                this.loading = false;
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this.error = error.response?.data?.message || 'Failed to add email to blocklist';
                this.loading = false;
            });
            return false;
        }
    }

    @action.bound
    public async deleteEmail(companyId: number, id: number): Promise<boolean> {
        this.loading = true;
        this.error = null;

        try {
            await this.service.deleteEmail(companyId, id);

            runInAction(() => {
                this.blocklistEntries = this.blocklistEntries.filter(entry => entry.id !== id);
                this.totalCount -= 1;
                this.loading = false;
            });

            return true;
        } catch (error) {
            runInAction(() => {
                this.error = error.response?.data?.message || 'Failed to delete email from blocklist';
                this.loading = false;
            });
            return false;
        }
    }

    @action.bound
    public clearError(): void {
        this.error = null;
    }

    @action.bound
    public setPage(page: number): void {
        this.currentPage = page;
    }

    @action.bound
    public reset(): void {
        this.blocklistEntries = [];
        this.loading = false;
        this.error = null;
        this.totalCount = 0;
        this.currentPage = 1;
    }
}
